import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Icon from "@mdi/react";
import { debounce } from "lodash";
import {
  mdiArrowTopRight,
  mdiFilterVariant,
  mdiHistory,
  mdiPaperclip,
  mdiPlus,
} from "@mdi/js";
import moment from "moment";
import { fetchGet, toQueryString } from "../../../service/api_client";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../context_providers/modal/loader";
import SearchFieldWidget from "../../../ui/new_components/search_field";
import { urls } from "../../../utils/urls";
import FilterDropDown from "../../../ui/new_components/common/filter_drop_down";
import Pagination from "../../../ui/pages/content_panel/ui/new_components/common/pagination";
import {
  NPDInwardStatus,
  NPDModel,
  NPDStatus,
  NPDType,
} from "../models/npd_model";
import DrawerWidget from "../../../context_providers/modal/drawer_widget";
import {
  ModalAlignment,
  ModalType,
} from "../../../context_providers/modal/modal_context";
import AttachmentSection from "../../prev_purchase_order/purchase_order_create/components/attachmnet";
import { AttachmentModel } from "../../prev_purchase_order/model/purchase_order_model";
import ExportNPD from "./components/NPD_Export";
import ModalDialog from "../../../ui/new_components/common/modal";
import NPDHistoryLogs from "./components/npd_history_logs";
import {
  ResponsiveListShell,
  FilterSheet,
  DateRangeFilter,
  MobileRecordCard,
  MobileRecordCardList,
  StatusBadge,
  EmptyState,
} from "../../../ui/new_components/common/responsive";

interface NPDListScreenProps {}

interface NPDFilter {
  npd_id?: string;
  project?: string;
  product?: string;
  type?: string[];
  status?: string[];
  inward_status?: string[];
  page: number;
  count: number;
  sort_order: string;
  all?: any;
  fromDate?: string;
  toDate?: string;
}

const defaultFilter = (): NPDFilter => {
  return {
    page: 1,
    count: 100,
    sort_order: "desc",
  };
};

const NPDListScreen: FunctionComponent<NPDListScreenProps> = () => {
  const navigation = useHistory();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd`;
  const { showToast } = useToast();
  const tempFilter = localStorage.getItem("npd_filter");
  const [page, setPage] = useState(1);
  const [count] = useState(50);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<NPDModel[]>([]);
  const [filter, setFilter] = useState<NPDFilter>(
    tempFilter ? JSON.parse(tempFilter) : defaultFilter(),
  );
  const [showExport, setShowExport] = useState(false);
  const [showAttachment, setShowAttachment] = useState<NPDModel>();
  const [showHistory, setShowHistory] = useState<string>();
  const [attachmentKey, setAttachmentKey] = useState(Math.random() * 10000);
  const [fetchedAt, setFetchedAt] = useState<Date>();
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: NPDFilter) => {
      if (prop) fetchData(prop);
      else setData([]);
    }, 300),
    [],
  );

  useEffect(() => {
    localStorage.setItem("npd_filter", JSON.stringify(filter));
  }, [filter]);

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  const fetchData = async (prop: NPDFilter) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      if (res.success) {
        setData(res?.data?.data);
        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
        setFetchedAt(new Date());
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const ExportData = async () => {
    try {
      const a = document.createElement("a");
      a.target = "_blank";
      a.href = `${
        process.env.REACT_APP_BACKEND_BASE_URL
      }/api/v3/erp/inventory/journal/sender_transfer/export${toQueryString({
        query: JSON.stringify({
          ...filter,
        }),
      })}`;

      // Delay the trigger to allow UI updates (if necessary)
      setTimeout(() => {
        setLoading(false);
        showToast({ type: ToastType.success, text: "Download started!" });
        a.click(); // Trigger the download
      }, 1000);
    } catch (error) {
      setLoading(false); // Make sure loading is set to false in case of an error
      showToast({ type: ToastType.error, text: "Failed to start download." });
      console.error("Download error:", error);
    }
  };

  const onSelect = (d: NPDModel) => {
    if (d.status === NPDStatus.final_review_pending) {
      navigation.push(urls.npd + "/update/" + d.id);
    } else {
      navigation.push(urls.npd + "/view/" + d.id);
    }
  };

  const onSelectAttchment = (d: AttachmentModel) => {
    window.open(d.url, "_blank");
  };
  const clearLocalStore = () => {
    // Remove from localStorage
    localStorage.removeItem("npd_filter");

    // Reset filter state
    const defaultFilterObj = defaultFilter();
    setFilter(defaultFilterObj);

    // Trigger UI updates and fetch
    debouncedHandler(defaultFilterObj);
    fetchData(defaultFilterObj);
  };

  const inwardStatusBadge = (status?: string) =>
    status ? (
      <StatusBadge
        status={status}
        kind="npd_inward"
        tone={status === NPDInwardStatus.completed ? "success" : "warning"}
      />
    ) : (
      <span className="text-gray-400">-</span>
    );

  return (
    <>
      {showExport && (
        <ModalDialog
          show={showExport}
          title="Export"
          onClose={() => setShowExport(false)}
        >
          <ExportNPD filter={filter} />
        </ModalDialog>
      )}
      {showAttachment && (
        <DrawerWidget
          data={{
            id: 1,
            title: "Attchments",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: (
              <AttachmentSection
                key={attachmentKey}
                attachments={showAttachment.attachments}
                onSelect={onSelectAttchment}
              />
            ),
          }}
          onClose={function (): void {
            setShowAttachment(undefined);
          }}
        ></DrawerWidget>
      )}
      {showHistory && (
        <DrawerWidget
          data={{
            id: 2,
            title: "History",
            type: ModalType.drawer,
            alignment: ModalAlignment.right,
            container: <NPDHistoryLogs npd_id={showHistory} />,
          }}
          onClose={function (): void {
            setShowHistory(undefined);
          }}
        ></DrawerWidget>
      )}

      <ResponsiveListShell
        title="NPD (New Product Development)"
        count={total}
        fetchedAt={fetchedAt}
        onRefresh={() => fetchData(filter)}
        search={
          <SearchFieldWidget
            placeholder="Search NPD ID"
            defaulValue={filter.npd_id ?? ""}
            onClear={() =>
              setFilter((p) => ({ ...p, npd_id: undefined, page: 1 }))
            }
            onSubmit={(value) =>
              setFilter((p) => ({ ...p, npd_id: value, page: 1 }))
            }
          />
        }
        actions={
          <>
            <button
              onClick={() => navigation.push(urls.npd + "/create")}
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
            >
              <Icon path={mdiPlus} className="h-4 w-4"></Icon>Create
            </button>
            <button
              onClick={() => setShowExport(true)}
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
            >
              <Icon path={mdiArrowTopRight} className="h-4 w-4"></Icon>Export
            </button>
            <button
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-xs text-white transform"
              onClick={() => clearLocalStore()}
            >
              Clear Filter
            </button>
          </>
        }
        filterToggle={
          <div className="flex gap-2 items-center">
            <h6 className="font-normal text-xs">Filters</h6>
            <div
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
            >
              <Icon path={mdiFilterVariant} className="w-4 h-4" />
            </div>
          </div>
        }
      >
        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={() => setShowFilter(false)}
          onReset={() => setFilter(defaultFilter())}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div>
            <label>Created Date</label>
            <DateRangeFilter
              compact
              showChips={false}
              fromDate={
                filter.fromDate
                  ? moment(filter.fromDate).format("YYYY-MM-DD")
                  : undefined
              }
              toDate={
                filter.toDate
                  ? moment(filter.toDate).format("YYYY-MM-DD")
                  : undefined
              }
              onFromChange={(v) =>
                setFilter((o) => ({
                  ...o,
                  fromDate: v ? v + "T00:00:00.000Z" : undefined,
                  page: 1,
                }))
              }
              onToChange={(v) =>
                setFilter((o) => ({
                  ...o,
                  toDate: v ? v + "T23:59:59.999Z" : undefined,
                  page: 1,
                }))
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold">Project</label>
            <SearchFieldWidget
              defaulValue={filter?.project}
              onClear={() => {
                setFilter({ ...filter, project: undefined });
                setPage(1);
              }}
              onSubmit={(val) => {
                setFilter({ ...filter, project: val });
                setPage(1);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold">Product</label>
            <SearchFieldWidget
              defaulValue={filter?.product}
              onClear={() => {
                setFilter({ ...filter, product: undefined });
                setPage(1);
              }}
              onSubmit={(val) => {
                setFilter({ ...filter, product: val });
                setPage(1);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold">Type</label>
            <FilterDropDown
              placeholder="type"
              onClearAll={() => setFilter({ ...filter, type: undefined })}
              onDelete={(type) => {
                setFilter({
                  ...filter,
                  type: filter?.type?.filter((f: any) => f !== type),
                });
              }}
              onSelect={(type) => {
                setFilter((old) => ({
                  ...old,
                  type: [...(old?.type ?? []), type],
                }));
              }}
              selectedOptions={filter?.type ?? []}
              options={Object.values(NPDType).map((t) => ({
                label: t,
                value: t,
              }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold">NPD Status</label>
            <FilterDropDown
              placeholder="npd_status"
              onClearAll={() => setFilter({ ...filter, status: undefined })}
              onDelete={(type) => {
                setFilter({
                  ...filter,
                  status: filter?.status?.filter((f: any) => f !== type),
                });
              }}
              onSelect={(type) => {
                setFilter((old) => ({
                  ...old,
                  status: [...(old?.status ?? []), type],
                }));
              }}
              selectedOptions={filter?.status ?? []}
              options={Object.values(NPDStatus).map((t) => ({
                label: t,
                value: t,
              }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-semibold">Inward Status</label>
            <FilterDropDown
              placeholder="inward_status"
              onClearAll={() =>
                setFilter({ ...filter, inward_status: undefined })
              }
              onDelete={(type) => {
                setFilter({
                  ...filter,
                  inward_status: filter?.inward_status?.filter(
                    (f: any) => f !== type,
                  ),
                });
              }}
              onSelect={(type) => {
                setFilter((old) => ({
                  ...old,
                  inward_status: [...(old?.inward_status ?? []), type],
                }));
              }}
              selectedOptions={filter?.inward_status ?? []}
              options={Object.values(NPDInwardStatus).map((t) => ({
                label: t,
                value: t,
              }))}
            />
          </div>
        </FilterSheet>

        {data?.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table className="w-full text-xs divide-y divide-gray-200 relative">
                  <thead className="text-xs py-2 sticky -top-1 bg-white z-10">
                    <tr className="border">
                      <th className="px-4 py-3 whitespace-nowrap text-center">
                        History
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        NPD ID
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Project
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Product
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Type
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Qty
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        NPD Status
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Inward Status
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Created By Name
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Created At
                      </th>
                      <th className="px-4 py-3 whitespace-nowrap text-left">
                        Attachments
                      </th>
                    </tr>
                  </thead>
                  <tbody className="">
                    {data.map((row, i) => (
                      <tr className="border" key={i}>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <button
                            title="History"
                            onClick={() => setShowHistory(row.id)}
                            className="cursor-pointer text-gray-500 hover:text-myPrimaryColor"
                          >
                            <Icon path={mdiHistory} size={0.8}></Icon>
                          </button>
                        </td>
                        <td
                          className="px-4 py-3 whitespace-nowrap text-left cursor-pointer text-blue-600 hover:underline"
                          role="button"
                          onClick={() => onSelect(row)}
                        >
                          {row.id}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row?.project?.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row?.product?.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row?.type}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row?.qty}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          <StatusBadge status={row?.status} kind="npd" />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {inwardStatusBadge(row?.inward_status)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {row?.created_by_name ?? " "}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          {moment(row.created_at).format("DD-MM-YYYY")}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-left">
                          <button
                            onClick={() => setShowAttachment(row)}
                            className="text-sm font-semibold text-blue-400 hover:text-blue-500 whitespace-nowrap flex items-center gap-1"
                          >
                            View
                            <span>({row?.attachments?.length ?? 0})</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {data.map((row, i) => (
                <MobileRecordCard
                  key={i}
                  onClick={() => onSelect(row)}
                  title={`${row.id}`}
                  status={<StatusBadge status={row?.status} kind="npd" />}
                  subStatus={inwardStatusBadge(row?.inward_status)}
                  fields={[
                    { label: "Project", value: row?.project?.name ?? "-" },
                    { label: "Product", value: row?.product?.name ?? "-" },
                    { label: "Type", value: row?.type ?? "-" },
                    { label: "Qty", value: row?.qty ?? "-" },
                    {
                      label: "Created By",
                      value: row?.created_by_name ?? "-",
                    },
                    {
                      label: "Created At",
                      value: moment(row.created_at).format("DD-MM-YYYY"),
                    },
                    {
                      label: "Attachments",
                      value: `${row?.attachments?.length ?? 0}`,
                    },
                  ]}
                  actions={[
                    {
                      icon: mdiHistory,
                      title: "History",
                      onClick: () => setShowHistory(row.id),
                    },
                    {
                      icon: mdiPaperclip,
                      title: "Attachments",
                      onClick: () => setShowAttachment(row),
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2">
              <Pagination
                itemCount={count}
                page={filter.page}
                total={total}
                count={filter.count}
                onChange={(page) => setFilter((o) => ({ ...o, page }))}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No NPDs found!"
            message="Try adjusting your search or filters."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget />}
    </>
  );
};

export default NPDListScreen;
